import React, { useState } from 'react';
import { StyledChip } from '@/components/Tag/styles';
import {
  IconButton,
  Autocomplete,
  TextField,
  Popper,
  Paper,
  Box,
  ClickAwayListener,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useSearchFilterNotesStore } from '@/stores/searchFilterNotesStore';
import { useAuthStore } from '@/stores/authStore';
import { getTagName, areTagsEqual } from '@/utils/tagUtils';

interface FilterTagsProps {
  tagCounts: Record<string, number>;
  onOpen?: () => void;
}

const FilterTags: React.FC<FilterTagsProps> = ({ tagCounts, onOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const { selectedTags, setSelectedTags } = useSearchFilterNotesStore();
  const { userData } = useAuthStore();

  const handleAddTag = (_: React.SyntheticEvent, newValue: unknown) => {
    const stringValues = Array.isArray(newValue)
      ? newValue.map((v) => (typeof v === 'string' ? v : String(v))).filter((v) => v)
      : [];
    setSelectedTags(stringValues);
  };

  const handleToggleAutocomplete = () => {
    const next = !openAutocomplete;
    setOpenAutocomplete(next);
    setForceOpen(true);
    if (next) onOpen?.();
  };

  const handleClickAway = () => {
    setOpenAutocomplete(false);
    setForceOpen(false);
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter((t) => !areTagsEqual(t, tag)));
  };

  const handleClearAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTags([]);
    setOpenAutocomplete(false);
    setForceOpen(false);
  };

  const hasActiveTags = selectedTags.length > 0;

  return (
    <Box display="flex" alignItems="center" gap={0.75} position="relative">
      <Tooltip title="Filter by tags">
        <IconButton
          onClick={handleToggleAutocomplete}
          size="small"
          aria-label="Filter by tags"
          sx={{
            width: 36,
            height: 36,
            border: '1px solid',
            borderColor: hasActiveTags ? 'primary.main' : 'divider',
            borderRadius: 1,
            backgroundColor: hasActiveTags ? 'rgba(43, 159, 255, 0.08)' : 'transparent',
            color: hasActiveTags ? 'primary.main' : 'action.active',
            '&:hover': {
              backgroundColor: hasActiveTags ? 'rgba(43, 159, 255, 0.12)' : 'action.hover',
            },
          }}
        >
          <LocalOfferOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {hasActiveTags && (
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" maxWidth={360}>
          {selectedTags.map((tag) => {
            const tagName = getTagName(tag);
            return (
              <Chip
                key={tagName}
                label={tagName}
                size="small"
                onDelete={() => handleRemoveTag(tag)}
                deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  height: 26,
                  fontSize: 12,
                  fontWeight: 500,
                  backgroundColor: 'rgba(43, 159, 255, 0.08)',
                  color: 'primary.main',
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'rgba(43, 159, 255, 0.3)',
                  '& .MuiChip-deleteIcon': {
                    color: 'primary.main',
                    opacity: 0.6,
                    '&:hover': {
                      opacity: 1,
                      color: 'primary.dark',
                    },
                  },
                }}
              />
            );
          })}
          {selectedTags.length > 1 && (
            <Tooltip title="Clear all tags">
              <IconButton size="small" onClick={handleClearAll} sx={{ padding: '2px' }}>
                <CloseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {openAutocomplete && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            sx={
              isMobile
                ? {
                    position: 'fixed',
                    top: 130,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 280,
                    zIndex: 1300,
                    backgroundColor: 'background.paper',
                    boxShadow: 6,
                    borderRadius: 2,
                  }
                : {
                    position: 'absolute',
                    top: 40,
                    left: 0,
                    width: 300,
                    zIndex: 10,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    borderRadius: 1,
                  }
            }
          >
            <Autocomplete
              disableListWrap
              multiple
              disableCloseOnSelect
              autoComplete
              autoFocus
              filterSelectedOptions
              options={
                Object.keys(tagCounts).length > 0
                  ? Object.keys(tagCounts)
                      .filter((tag) => tagCounts[tag] > 0)
                      .sort((a, b) => a.localeCompare(b))
                  : (userData?.tags
                      ?.map((tag) => getTagName(tag))
                      .slice()
                      .sort((a, b) => a.localeCompare(b)) ?? [])
              }
              open={forceOpen}
              onOpen={() => setForceOpen(true)}
              onClose={() => setForceOpen(true)}
              value={selectedTags.map((tag) => getTagName(tag))}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) => {
                  const optionStr = getTagName(option);
                  return optionStr.toLowerCase().includes(inputValue.toLowerCase());
                })
              }
              onChange={(_, newValue) => {
                handleAddTag(_, newValue as string[]);
              }}
              getOptionLabel={(option) => getTagName(option)}
              isOptionEqualToValue={(option, value) => areTagsEqual(option, value)}
              renderInput={(props) => (
                <TextField
                  {...props}
                  placeholder="Search tags"
                  size="small"
                  slotProps={{
                    input: {
                      ...props.InputProps,
                      startAdornment: <></>,
                      endAdornment: (
                        <Box position="absolute" right={5} top={0} bottom={0} display="flex" alignItems="center">
                          <SearchRoundedIcon color="disabled" fontSize="small" />
                        </Box>
                      ),
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                const optionStr = getTagName(option);
                const count = tagCounts[optionStr] || 0;
                return (
                  <li key={key} {...rest}>
                    <StyledChip label={`${optionStr} (${count})`} size="small" icon={<LocalOfferOutlinedIcon />} />
                  </li>
                );
              }}
              PopperComponent={(props) => <Popper {...props} disablePortal />}
              PaperComponent={(props) => <Paper {...props} />}
              ListboxProps={{
                style: {
                  maxHeight: isMobile ? 300 : 250,
                  padding: '4px 0',
                },
                sx: {
                  '& .MuiAutocomplete-option': {
                    padding: '4px 8px',
                    minHeight: 'unset',
                  },
                },
              }}
            />
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default FilterTags;
